import { IDeveloperSettings } from 'dromo-uploader-react'
import { useStyleOverrides } from '.'
import { useSelector } from 'react-redux'
import { selectCurrentUser } from '@user/store/authorization'
import { useMemo, useState } from 'react'
import { environment } from '@sales-center/common'
import { useFieldSettings } from './useFieldSettings'
import { ImportUtilityModel } from '../AddImportUtilityDrawer/typings'
import { useColumnHooks } from './useColumnHooks'
import { useRowHooks } from './rowHooks/useRowHooks'
import { ImportEntityType } from '../typings'
import { ServiceConfig } from '@user/store/services/config'

const licenseKey = ServiceConfig.dromoLicenseKey || ''

export function useData() {
  const styleOverrides = useStyleOverrides()

  const currentUser = useSelector(selectCurrentUser)

  const [uploadId, setUploadId] = useState<string | null>(null)

  const [showDromo, setShowDromo] = useState<boolean>(false)
  const [showConfirmPopup, setShowConfirmPopup] = useState<boolean>(false)

  const [importDefaultValues, setImportDefaultValues] =
    useState<ImportUtilityModel | null>(null)
  const fields = useFieldSettings(importDefaultValues?.entityType)

  const rowHooks = useRowHooks(importDefaultValues)
  const columnHooks = useColumnHooks(importDefaultValues)

  const entityTypeTitle = useMemo(() => {
    switch (importDefaultValues?.entityType) {
      case ImportEntityType.DraftLead:
        return 'Pending Leads'
      case ImportEntityType.Lead:
        return 'Leads'
      case ImportEntityType.Opportunity:
        return 'Opportunities'
      default:
        return ''
    }
  }, [importDefaultValues?.entityType])

  const settings: IDeveloperSettings = {
    allowEmptySubmit: false,
    backendSync: false,
    backendSyncMode: 'FULL_DATA',
    developmentMode: environment !== 'prod',
    importIdentifier: `env=${environment}`,
    manualInputDisabled: true,
    styleOverrides: styleOverrides,
    title: `${entityTypeTitle} Import`,
    maxRecords: 10001,
  }

  const user = useMemo(
    () => ({
      id: currentUser.userId,
      name: currentUser.fullName,
      email: currentUser.emailAddress,
      companyId: importDefaultValues?.tempApiKey,
      companyName: currentUser.companyName,
    }),
    [
      currentUser.companyName,
      currentUser.emailAddress,
      currentUser.fullName,
      currentUser.userId,
      importDefaultValues?.tempApiKey,
    ],
  )

  return {
    fields,
    licenseKey,
    settings,
    user,
    uploadId,
    setUploadId,
    showDromo,
    setShowDromo,
    columnHooks,
    rowHooks,
    setImportDefaultValues,
    importDefaultValues,
    showConfirmPopup,
    setShowConfirmPopup,
  }
}

export type UseData = ReturnType<typeof useData>
